import React from 'react';
import { BsQuestionCircleFill } from 'react-icons/bs';
import { MdEdit } from 'react-icons/md';
import { accentUtils, t } from '../../services/HelperService';
import { AccentMarkdown, containsBlockMarkdown } from './AccentMarkdown';

const safeText = (v) => (v ?? '').toString();

// The explicit action a user message was sent with, rendered as an icon inside the
// bubble: green ? for "ask a question", orange pencil for the update action. The
// clicked button's label rides the message as intentLabel and becomes the tooltip.
const intentIcons = {
  ask: { Icon: BsQuestionCircleFill, color: '#2e7d32' },
  change: { Icon: MdEdit, color: '#ef6c00' },
};

const UserIntentIcon = ({ message }) => {
  const intent = safeText(message?.intent).toLowerCase();
  const entry = intentIcons[intent];
  if (!entry) return null;
  const { Icon } = entry;
  return (
    <span title={safeText(message?.intentLabel) || undefined} style={{ marginRight: 6, verticalAlign: '-2px', display: 'inline-flex' }}>
      <Icon size={14} color={entry.color} />
    </span>
  );
};

const roleBubbleStyles = {
  user: {
    background: '#3b82f620',
    border: '1px solid #3b82f640',
    justifySelf: 'end',
    borderRadius: '12px 12px 4px 12px',
  },
  assistant: {
    background: '#e8f5e9',
    border: '1px solid #c8e6c9',
    justifySelf: 'start',
    borderRadius: '12px 12px 12px 4px',
  },
  system: {
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    justifySelf: 'start',
    borderRadius: '12px 12px 12px 4px',
    fontStyle: 'italic',
    opacity: 0.85,
  },
  // Amber: cautions and disclosed limitations — the turn succeeded, but read this.
  warning: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    justifySelf: 'start',
    borderRadius: '12px 12px 12px 4px',
    color: '#9a6700',
  },
  error: {
    background: '#fce4ec',
    border: '1px solid #f8bbd0',
    justifySelf: 'start',
    borderRadius: '12px 12px 12px 4px',
    color: '#b71c1c',
  },
};

const roleLabels = {
  user: null,
  assistant: 'AI',
  system: 'System',
  warning: 'Note',
  error: 'Error',
};

// The automated review renders as a graded result, not a binary error: the palette
// follows the score band so a 95 with one advisory no longer looks like a failure.
const reviewPalette = (score, failed) => {
  if (failed || (typeof score === 'number' && score < 70)) {
    return { background: '#fdecea', border: '1px solid #f5c6c0', accent: '#b71c1c', bar: '#e53935' };
  }
  if (typeof score === 'number' && score < 90) {
    return { background: '#fff8e1', border: '1px solid #ffe082', accent: '#9a6700', bar: '#f9a825' };
  }
  return { background: '#e8f5e9', border: '1px solid #a5d6a7', accent: '#1b5e20', bar: '#43a047' };
};

const confidenceLabels = {
  high: 'high confidence',
  medium: 'medium confidence',
  low: 'low confidence',
};

/**
 * Grouped warning/info notices (mostly honestly-disclosed API limitations), collapsed
 * behind a count so several caveats read as one line instead of a wall of amber bubbles.
 */
const CollapsibleNotices = ({ notices }) => {
  const [open, setOpen] = React.useState(false);
  const list = (Array.isArray(notices) ? notices : [])
    .map((n) => ({ text: safeText(n?.text), severity: safeText(n?.severity).toLowerCase() }))
    .filter((n) => n.text);
  if (list.length === 0) return null;

  return (
    <div>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#9a6700', userSelect: 'none' }}
      >
        {open ? '\u25BE' : '\u25B8'} {t('Limitations & notes')} ({list.length})
      </div>
      {open && (
        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
          {list.map((n, ix) => (
            <li
              key={`n-${ix}`}
              style={n.severity === 'info' ? { opacity: 0.7 } : { color: '#9a6700' }}
            >
              {n.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ReviewBubble = ({ message }) => {
  const review = message?.review ?? {};
  const score = typeof review.score === 'number' ? Math.max(0, Math.min(100, review.score)) : null;
  const failed = !!review.failed;
  const defects = (Array.isArray(review.defects) ? review.defects : []).map(safeText).filter(Boolean);
  const advisories = (Array.isArray(review.advisories) ? review.advisories : []).map(safeText).filter(Boolean);
  const confidence = confidenceLabels[safeText(review.confidence).toLowerCase()] ?? null;
  const palette = reviewPalette(score, failed);

  return (
    <div
      style={{
        maxWidth: '85%',
        padding: '8px 10px',
        fontSize: 12,
        lineHeight: 1.4,
        background: palette.background,
        border: palette.border,
        borderRadius: '12px 12px 12px 4px',
        justifySelf: 'start',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 700, color: palette.accent }}>
          {score != null ? `${t('Review')} ${score}/100` : t('Review')}
        </span>
        {!!confidence && <span style={{ fontSize: 11, opacity: 0.7 }}>{t(confidence)}</span>}
      </div>
      {score != null && (
        <div style={{ height: 5, background: '#00000014', borderRadius: 999, overflow: 'hidden', marginBottom: 6 }}>
          <div style={{ height: '100%', width: `${Math.max(4, score)}%`, background: palette.bar, borderRadius: 999 }} />
        </div>
      )}
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{safeText(message?.text)}</div>
      {defects.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 16, color: palette.accent }}>
          {defects.map((d, ix) => <li key={`d-${ix}`}>{d}</li>)}
        </ul>
      )}
      {advisories.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 16, opacity: 0.75 }}>
          {advisories.map((a, ix) => <li key={`a-${ix}`}>{a}</li>)}
        </ul>
      )}
      {Array.isArray(review.notices) && review.notices.some((n) => safeText(n?.text)) && (
        <div style={{ marginTop: 6 }}>
          <CollapsibleNotices notices={review.notices} />
        </div>
      )}
    </div>
  );
};

export const AccentPromptHistory = ({
  messages,
  maxHeight = 260,
  fillHeight = false,
  renderMessageExtra,
}) => {
  const list = Array.isArray(messages) ? messages : [];
  const endRef = React.useRef(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [list.length]);

  if (list.length === 0) {
    return (
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        {t('No conversation yet.')}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        ...(fillHeight ? { height: '100%', minHeight: 200 } : { maxHeight }),
        overflowY: 'auto',
        padding: 4,
      }}
    >
      {list.map((m, ix) => {
        const text = safeText(m?.text);
        if (accentUtils.isEmpty(text)) return null;

        const role = (m?.role ?? 'user').toString().toLowerCase();
        // "tool" messages are hidden server round records (query planner briefs/answers)
        // that only exist so the next request can round-trip them — never rendered.
        if (role === 'tool') return null;

        // Grouped notices without a review verdict to ride on: one amber bubble holding
        // the same collapsible list the review bubble would have carried.
        if (role === 'notices') {
          return (
            <div
              key={m?.id ?? `msg-${ix}`}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.5, marginBottom: 1, paddingLeft: 4, paddingRight: 4 }}>
                {t('Notes')}
              </div>
              <div
                style={{
                  maxWidth: '85%',
                  padding: '6px 10px',
                  fontSize: 12,
                  lineHeight: 1.4,
                  ...roleBubbleStyles.warning,
                }}
              >
                <CollapsibleNotices notices={m?.notices} />
              </div>
              {renderMessageExtra?.(m)}
            </div>
          );
        }

        // Automated review verdicts get a graded, score-colored bubble of their own.
        if (role === 'review') {
          return (
            <div
              key={m?.id ?? `msg-${ix}`}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.5, marginBottom: 1, paddingLeft: 4, paddingRight: 4 }}>
                {t('Review')}
              </div>
              <ReviewBubble message={m} />
              {renderMessageExtra?.(m)}
            </div>
          );
        }

        const bubbleStyle = roleBubbleStyles[role] ?? roleBubbleStyles.user;
        const label = roleLabels[role] ?? null;

        // Assistant answers always render as markdown (so inline emphasis like
        // **3,221 jobs** displays bold); block markdown (data tables, headings) also
        // leaves the bubble form for a full-width card so tables get real column room.
        const isAssistant = role === 'assistant';
        const isMarkdownCard = isAssistant && containsBlockMarkdown(text);

        return (
          <div
            key={m?.id ?? `msg-${ix}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isMarkdownCard ? 'stretch' : (role === 'user' ? 'flex-end' : 'flex-start'),
            }}
          >
            {!!label && (
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.5, marginBottom: 1, paddingLeft: 4, paddingRight: 4 }}>
                {label}
              </div>
            )}
            <div
              style={{
                wordBreak: 'break-word',
                fontSize: 12,
                lineHeight: 1.4,
                ...(isMarkdownCard
                  ? {
                    width: '100%',
                    padding: '8px 12px',
                    background: '#ffffff',
                    border: '1px solid #dee2e6',
                    borderRadius: 8,
                    overflowX: 'auto',
                  }
                  : {
                    maxWidth: '85%',
                    padding: '6px 10px',
                    // Markdown supplies its own paragraph/line-break handling; pre-wrap
                    // would render the parser's inter-block newlines as blank lines.
                    ...(isAssistant ? {} : { whiteSpace: 'pre-wrap' }),
                    ...bubbleStyle,
                  }),
              }}
            >
              {isAssistant
                ? <AccentMarkdown text={text} />
                : (
                  <>
                    {role === 'user' && <UserIntentIcon message={m} />}
                    {text}
                  </>
                )}
            </div>
            {renderMessageExtra?.(m)}
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
};
